/**
 * S-3057A: Overlay Controller
 * S-3057E: Canary rollout with health metrics
 * 
 * Routes to v1 or v2 overlay based on feature flag and canary logic.
 * V2 is enabled for canary users when flag is true:
 * - Internal users: @nodoublesgolf.co
 * - Test accounts: jamesrmccarthy@gmail.com
 */

import React, { useEffect } from 'react';
import { useAuth } from '@/hooks/use-auth';
import OverlayCanvas from '../swing-lab/analysis/OverlayCanvas'; // v1
import { OverlayCanvasV2Simple } from './OverlayCanvasV2Simple'; // v2 - simplified to match V1 pattern
import { initHealthMetricsAPI, healthMetrics } from './healthMetrics'; // S-3057E
import type { PoseData } from './types';

interface OverlayControllerProps {
  // V1 props (existing overlay)
  videoRef: React.RefObject<HTMLVideoElement>;
  getLatestFrame: () => unknown;
  getFrameAtTime: (time: number) => unknown;
  isLiveCapture: boolean;
  visible: boolean;
  opacity: number;
  showSkeleton?: boolean;
  handsOnly?: boolean;
  
  // V2 props (new overlay - only used if feature flag enabled)
  poseData?: PoseData | null;
}

export function OverlayController(props: OverlayControllerProps) {
  const { user } = useAuth();
  
  // S-3057E: Canary logic
  // Feature flag must be enabled (kill switch)
  const v2Env = import.meta.env.VITE_TELEMETRY_V2 === 'true';
  
  // DEV/TEST OVERRIDE: Query parameter can force V1 or V2
  const urlParams = new URLSearchParams(window.location.search);
  const telemetryParam = urlParams.get('telemetryV2');
  
  // If query param is set, it overrides everything (for testing)
  // ?telemetryV2=true → force V2
  // ?telemetryV2=false → force V1
  // no param → use env flag
  const v2Override = telemetryParam !== null 
    ? telemetryParam === 'true' 
    : v2Env;
  
  // Canary user check: internal domain OR specific test accounts
  const isCanaryUser = 
    user?.email?.endsWith('@nodoublesgolf.co') ||
    user?.email === 'jamesrmccarthy@gmail.com';
  
  const v2Canary = v2Override && isCanaryUser;
  
  // Final decision: use V2 only if canary approved AND pose data available
  const useV2 = v2Canary && !!props.poseData;

  // S-3057E: Initialize health metrics API once (dev mode only)
  useEffect(() => {
    if (import.meta.env.DEV) {
      initHealthMetricsAPI();
    }
  }, []);

  console.log('[OverlayController] Selected version:', useV2 ? 'v2' : 'v1', {
    BUILD_ID: '20153BA_MEMOIZATION_FIX',
    featureFlag: v2Env,
    queryParam: telemetryParam,
    effectiveFlag: v2Override,
    canaryApproved: v2Canary,
    userEmail: user?.email,
    hasPoseData: !!props.poseData,
  });

  // V2 path: Use simple overlay that uses V1's getFrameAtTime
  if (useV2) {
    return (
      <OverlayCanvasV2Simple
        videoRef={props.videoRef}
        getFrameAtTime={props.getFrameAtTime}
        visible={props.visible}
        opacity={props.opacity}
      />
    );
  }

  // V1 path: Use existing overlay (default for everyone else)
  return (
    <OverlayCanvas
      videoRef={props.videoRef}
      getLatestFrame={props.getLatestFrame}
      getFrameAtTime={props.getFrameAtTime}
      isLiveCapture={props.isLiveCapture}
      visible={props.visible}
      opacity={props.opacity}
      showSkeleton={props.showSkeleton}
      handsOnly={props.handsOnly}
    />
  );
}

